export type SectionId = 'eligibility' | 'howToClaim' | 'notIncluded' | 'coverage' | 'faqs'
export type CoverageTab = 'covered' | 'notCovered'

export interface SectionDefinition {
  id: SectionId
  label: string
}

export const SECTIONS: SectionDefinition[] = [
  { id: 'eligibility', label: 'Eligibility criteria' },
  { id: 'howToClaim', label: 'How to claim' },
  { id: 'notIncluded', label: 'What is not included' },
  { id: 'coverage', label: 'Policy coverage details' },
  { id: 'faqs', label: 'FAQs' }
]

export const ELIGIBILITY_ITEMS: Array<{ icon: 'flag' | 'PalmTree' | 'clock'; text: string }> = [
  { icon: 'flag', text: 'Only for Indian citizens' },
  { icon: 'PalmTree', text: 'Only for tourist, business and student visa holders' },
  { icon: 'clock', text: 'Claim up to 48 hrs before departure' }
]

export const CLAIM_STEPS: Array<{ icon: 'cross' | 'upload' | 'refund'; text: string }> = [
  { icon: 'cross', text: 'Choose cancellation reason as "Visa rejection"' },
  { icon: 'upload', text: 'Upload all required documents within 7 days for refund approval' },
  { icon: 'refund', text: 'Get refund within 5-7 days after the approval' }
]

export const EXCLUSIONS: string[] = [
  'Any other visa types except tourist, business and student visas',
  'Delay in visa application or embassy approval',
  'Cancellations within 48hrs of departure',
  'Cleartrip platform charges are not refundable'
]

export const COVERED_ITEMS: string[] = [
  'Failure to justify the purpose and conditions of the intended stay, including incomplete or unclear travel plans.',
  'Insufficient proof of sufficient funds for the stay, return travel, or onward journey.',
  'Alert issued in the Schengen Information System (SIS) regarding public health threats or security risks.',
  'Doubts raised by the embassy regarding the authenticity or accuracy of supporting documents.',
  'Applying for a visa too close to the travel date without a valid justification.',
  'Reasonable suspicion that the applicant does not intend to leave the destination country before the visa expiry.',
  'Presence of a medical condition that may prevent travel or pose a risk to public health.'
]

export const NOT_COVERED_ITEMS: string[] = [
  'Submission of any Incomplete, insufficient, false, or forged Documents for visa  processing.',
  'Passport Invalidity.',
  'If Insured Person’s past Visa application was rejected  ',
  'Past or current criminal Actions, litigations, cases etc. against the Insured Person  leading to vis rejection ',
  'Insured Person missing the pre-Scheduled Appointment for Visa application. ',
  'An alert issued in the Schengen Information system (SIS) for the purpose of  refusing entry by - a. One or more member states consider you to be a threat to public policy or internal security ',
  'Not provided proof of possession of adequate and valid travel medical insurance  ',
  'Revocation of the visa was requested by the visa holder  ',
  "Passport issues: The applicant's passport might be about to expire or might not  have enough blank page  ",
  'Criminal record: The applicant might have a criminal record, which could be a  safety concern  ',
  'Prior immigration or visa violations - History of overstaying visas or violating  immigration laws  ',
  'Delays in visa from embassy are not covered.',
]
export interface FAQListItem {
  content: string
}

export interface FAQAnswerSection {
  text?: string
  list?: FAQListItem[]
}

export interface FAQ {
  question: string
  answer: FAQAnswerSection[]
}

export const FAQS: FAQ[] = [
  {
    question: "What is Visa Denial Cover?",
    answer: [
      {
        text: "Visa Denial Cover is a Cleartrip program that ensures a full refund minus the convenience fee on your flight ticket if your tourist, business or student visa application is officially rejected by the embassy/consulate. This means you can book without worrying about losing money in case your visa isn't approved."
      }
    ]
  },
  {
    question: "Who is eligible for this cover?",
    answer: [
      {
        text: "You are eligible if all of the below conditions are true/fulfilled:",
        list: [
          {
            content: "i. You booked your <b>international flight</b> via Cleartrip departing from <b>India</b>. And you are holding a <b>valid Indian Passport</b>."
          },
          {
            content: "ii. You applied for a <b>tourist, business or student visa</b> and it was <b>rejected</b> by the embassy/consulate."
          },
          {
            content: "iii. Your flight ticket is <b>partially or fully refundable and eligible under this program</b>."
          },
          {
            content: "iv. You submit all required documents supporting your visa rejection within the required timeframe."
          }
        ]
      }
    ]
  },
  {
    question: "What visa types are covered?",
    answer: [
      {
        text: "This cover is <b>applicable for tourist, business and student visas.</b> Other types of visas (work, dependent, or transit visas) are <b>not covered.</b>"
      }
    ]
  },
  {
    question: "What if my visa application is delayed instead of rejected?",
    answer: [
      {
        text: "Delays in visa processing are <b>not covered.</b> The refund is applicable <b>only if your visa is officially rejected by the embassy/consulate.</b>"
      }
    ]
  },
  {
    question: "What if my booking has multiple passengers, and only one person's visa is rejected?",
    answer: [
      {
        text: "If multiple passengers are booked together and only <b>one person's visa is denied</b>, the refund will apply only for that passenger, provided the airline allows <b>partial cancellations. Please refer to the airline terms and conditions on partial cancellations.</b>"
      }
    ]
  },
  {
    question: "Is the Visa Denial Cover refund processed automatically?",
    answer: [
      {
        text: "No, you <b>must cancel</b> the ticket <b>at least 48 hours before departure</b> via the Cleartrip app or website to claim the refund."
      }
    ]
  },
  {
    question: "What is the Refund Claim procedure for flights?",
    answer: [
      {
        list: [
          {
            content: "<b>Step 1:</b> The customer must click on the My Trips section on Cleartrip App / Website and raise a request for cancellation by selecting <b>'Due to visa rejection'</b> as the reason. Cancellation must be done <b>48 hours before the departure time</b>."
          },
          {
            content: "<b>Step 2:</b> On cancellation, a partial refund (excluding Cleartrip & airline cancellation charges) will be processed within <b>48 hours</b>."
          },
          {
            content: "<b>Step 3:</b> You will receive an email to upload the required documents on a link to claim the Visa Denial Cover benefit. You can file for a claim by uploading the required documents within <b>10 days after cancelling the flight</b>."
          },
          {
            content: "<b>Step 4:</b> On receiving the documents, Cleartrip will validate and process the remaining refund equivalent to cancellation charges for eligible and approved cases. Once validated, the refund will be processed within <b>5-7 working days</b> to the original payment mode. The Cleartrip convenience fee is <b>non-refundable</b>."
          }
        ]
      }
    ]
  },
  {
    question: "What documents do I need to submit to claim a refund?",
    answer: [
      {
        text: "a. To file a claim, you need to upload the following documents on the Cleartrip claims portal:",
        list: [
          {
            content: "i. <b>Visa Rejection Letter</b> from the embassy/consulate stating the reason for rejection. Note: For Japan/Turkey/China, in case of absence of a Visa Rejection Letter from the embassy, one can arrange for the consulate's mail confirming the visa rejection which will be considered as a valid document in such a case."
          },
          {
            content: "ii. <b>Passport Copy</b> – Visa-stamped page where the visa rejection is mentioned *(For countries where rejection is stamped directly on the passport).* "
          },
          {
            content: "iii. <b>Ticket Copy -</b> The ticket copy as received from Cleartrip at the time of cancellation, in a PDF file."
          },
          {
            content: "iv. Cancellation Breakdown - the cancellation breakdown as received from Cleartrip at the time of cancellation, in a PDF file or email screenshot."
          }
        ]
      },
      {
        text: "b. If <b>document re-validation</b> is required, the customer must resubmit the document <b>within the next 10 days</b>."
      }
    ]
  },
  {
    question: "Do I need to upload my cancellation breakdown?",
    answer: [
      {
        text: "Yes, you need to download the cancellation refund breakdown sent to you at the time of cancellation, in a clear PDF file format. This cancellation breakdown covers your airline cancellation charges, processed refund and the partially refunded amount sent to you. You need to ensure that Cleartrip's email address, your personal email address, and the email body are clearly visible in a SINGLE pdf file/screenshot."
      }
    ]
  },
  {
    question: "Is it available on non-India originating flights?",
    answer: [
      {
        text: "No, our Visa Denial Cover is only available on flights departing from India. A roundtrip flight is only eligible if the origin destination is from India. So your roundtrip from India to another country is covered, but not vice versa."
      }
    ]
  },
  {
    question: "Do I get the full refund immediately after cancellation?",
    answer: [
      {
        text: "You will get the partial refund after deducting airline cancellation charges and platform fees. You will then receive an email confirmation with a link. Upon uploading your rejection proofs from the embassy, your ticket copy, and your KYC document, your claim will be taken up for further processing."
      }
    ]
  },
  {
    question: "What if I don't get a Visa Rejection Letter from the embassy?",
    answer: [
      {
        text: "In case you don't get a detailed visa rejection letter from the embassy (such as China or Japan), you can submit the email screenshot of the visa rejection email. You need to ensure that the sender's email address, your personal email address, and the email body are clearly visible in a SINGLE pdf file/screenshot. Please be advised that unclear details may lead to rejection of claim by the insurer."
      }
    ]
  },
  {
    question: "Is it applicable on non-refundable flights?",
    answer: [
      {
        text: "No, it is only applicable on partially or fully refundable flights."
      }
    ]
  },
  {
    question: "What happens if I cancel my ticket directly with the airline?",
    answer: [
      {
        text: "You will not be able to claim the Visa Denial Cover from airline websites/apps. To claim the refund, you need to cancel your booking on Cleartrip."
      }
    ]
  }
]

export const CTA_URL = 'https://www.cleartrip.com/all-offers/visa-denial-cover/'

